import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, Row, Col, Panel, Button, Label } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dateFormat from 'dateformat';
import { fetchApi } from '../../Network/NetworkService';

const TYPE_LABELS = {
    'medecine-generale': { label: 'Généraliste', style: 'primary' },
    dermatologie: { label: 'Dermato', style: 'success' },
    acne: { label: 'Acné', style: 'success' },
    auditif: { label: 'Auditif', style: 'info' },
    ronflement: { label: 'Ronflement', style: 'info' },
    psychiatre: { label: 'Psychiatre', style: 'warning' },
    psychologie: { label: 'Psycho', style: 'warning' },
    gynecologie: { label: 'Gynéco', style: 'danger' },
    dentistry: { label: 'Dentaire', style: 'default' },
    'traitement-anti-obesite': { label: 'Anti-obésité', style: 'default' },
    'evaluation-nutritionnelle': { label: 'Nutrition', style: 'default' },
    postTeleconsultation: { label: 'Post-télé', style: 'default' }
};

const renderType = (type) => {
    const meta = TYPE_LABELS[type] || { label: type, style: 'default' };
    return <Label bsStyle={meta.style}>{meta.label}</Label>;
};

const hoursLeftStyle = (hoursLeft) => {
    if (hoursLeft <= 12) return { color: '#d9534f', fontWeight: 600 };
    if (hoursLeft <= 48) return { color: '#f0ad4e', fontWeight: 600 };
    return { color: '#5cb85c' };
};

const formatTimeRemaining = (hoursLeft) => {
    if (hoursLeft < 24) return `${hoursLeft}h`;
    const days = Math.floor(hoursLeft / 24);
    return `${days} jour${days > 1 ? 's' : ''} (${hoursLeft}h)`;
};

const STRIPE_STATUS_LABELS = {
    requires_capture: { text: 'Capturable', style: 'success' },
    requires_payment_method: { text: 'Sans moyen de paiement', style: 'danger' },
    requires_confirmation: { text: 'À confirmer', style: 'warning' },
    requires_action: { text: 'Action requise', style: 'warning' },
    processing: { text: 'En cours', style: 'info' },
    canceled: { text: 'Annulé', style: 'danger' },
    succeeded: { text: 'Déjà capturé', style: 'default' }
};

const PendingCapture = () => {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ count: 0, totalExpectedEur: 0, byType: {}, windowDays: 7 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stripeStatus, setStripeStatus] = useState({}); // { [rid]: { loading, data, error } }

    const fetchList = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchApi('/pending-capture/list', 'GET');
            if (res && res.success) {
                setRows(res.data.requests || []);
                setSummary({
                    count: res.data.count || 0,
                    totalExpectedEur: res.data.totalExpectedEur || 0,
                    byType: res.data.byType || {},
                    windowDays: res.data.windowDays || 7
                });
            } else {
                setError((res && res.message) || 'Erreur de chargement');
            }
        } catch (err) {
            setError(`Erreur: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchList(); }, [fetchList]);

    const checkStripe = useCallback(async (rid) => {
        setStripeStatus((prev) => ({ ...prev, [rid]: { loading: true } }));
        try {
            const res = await fetchApi(`/pending-capture/check/${rid}`, 'GET');
            if (res && res.success) {
                setStripeStatus((prev) => ({ ...prev, [rid]: { loading: false, data: res.data } }));
            } else {
                setStripeStatus((prev) => ({ ...prev, [rid]: { loading: false, error: (res && res.message) || 'Erreur' } }));
            }
        } catch (err) {
            setStripeStatus((prev) => ({ ...prev, [rid]: { loading: false, error: err.message } }));
        }
    }, []);

    const typeBreakdown = useMemo(() => {
        return Object.entries(summary.byType || {}).sort((a, b) => b[1] - a[1]);
    }, [summary.byType]);

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Capture en attente</h2>
                    <p className="text-muted">
                        Requêtes en <code>waiting</code> avec un PaymentIntent Stripe
                        encore dans la fenêtre d'autorisation de {summary.windowDays} jours.
                        Au-delà, l'autorisation expire et le médecin ne peut plus capturer
                        le paiement. Triées par ancienneté (les plus urgentes d'abord).
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible">
                            <button type="button" className="close" onClick={() => setError(null)}>
                                <span>&times;</span>
                            </button>
                            {error}
                        </div>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Requêtes capturables</h5>
                        <h2 style={{ margin: 0 }}>{summary.count}</h2>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Montant estimé total</h5>
                        <h2 style={{ margin: 0, color: '#5cb85c' }}>
                            {summary.totalExpectedEur.toFixed(2)} €
                        </h2>
                    </Panel.Body></Panel>
                </Col>
                <Col md={6}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Répartition par type</h5>
                        {typeBreakdown.length === 0 && <span className="text-muted">—</span>}
                        {typeBreakdown.map(([type, count]) => (
                            <span key={type} style={{ marginRight: 8 }}>
                                {renderType(type)} <strong>{count}</strong>
                            </span>
                        ))}
                    </Panel.Body></Panel>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col xs={12}>
                    <Button bsStyle="primary" onClick={fetchList} disabled={loading}>
                        {loading ? 'Chargement...' : 'Actualiser'}
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Panel>
                        <Panel.Heading>
                            <h4>Liste ({rows.length})</h4>
                        </Panel.Heading>
                        <Panel.Body style={{ overflowX: 'auto' }}>
                            {loading && <p className="text-muted">Chargement...</p>}
                            {!loading && rows.length === 0 && (
                                <p className="text-muted">
                                    Aucune requête en attente avec un PaymentIntent
                                    encore capturable dans la fenêtre de {summary.windowDays} jours.
                                </p>
                            )}
                            {!loading && rows.length > 0 && (
                                <table className="table table-bordered table-hover table-condensed">
                                    <thead>
                                        <tr>
                                            <th>Créée</th>
                                            <th>Expire</th>
                                            <th style={{ textAlign: 'right' }}>Reste</th>
                                            <th>Type</th>
                                            <th>Patient</th>
                                            <th>Contact</th>
                                            <th style={{ textAlign: 'right' }}>Montant</th>
                                            <th>Source</th>
                                            <th>PaymentIntent</th>
                                            <th>Statut Stripe</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const stripeRow = stripeStatus[r._id];
                                            const stripeMeta = stripeRow && stripeRow.data
                                                ? STRIPE_STATUS_LABELS[stripeRow.data.status] || { text: stripeRow.data.status, style: 'default' }
                                                : null;
                                            return (
                                                <tr key={r._id}>
                                                    <td>{dateFormat(r.createdAt, 'dd/mm HH:MM')}</td>
                                                    <td>{dateFormat(r.expiresAt, 'dd/mm HH:MM')}</td>
                                                    <td style={{ textAlign: 'right', ...hoursLeftStyle(r.hoursLeft) }}>
                                                        {formatTimeRemaining(r.hoursLeft)}
                                                    </td>
                                                    <td>
                                                        {renderType(r.type)}
                                                        {r.priority && <Label bsStyle="danger" style={{ marginLeft: 4 }}>P</Label>}
                                                        {r.skycallVoice && <Label bsStyle="info" style={{ marginLeft: 4 }}>Voix</Label>}
                                                    </td>
                                                    <td>
                                                        {r.user ? (
                                                            <Link to={`/users/detail/${r.user._id}`}>
                                                                {r.user.name || r.user.email || 'Patient'}
                                                            </Link>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {r.user && (
                                                            <small>
                                                                {r.user.email && <div>{r.user.email}</div>}
                                                                {r.user.phone && <div>{r.user.phone}</div>}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {r.expectedAmountEur ? `${r.expectedAmountEur.toFixed(2)} €` : '—'}
                                                    </td>
                                                    <td>
                                                        <small>{r.utm_source || '—'}</small>
                                                    </td>
                                                    <td>
                                                        <small style={{ fontFamily: 'monospace' }}>
                                                            {r.paymentIntentId
                                                                ? r.paymentIntentId.slice(0, 14) + '…'
                                                                : '—'}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        {stripeRow && stripeRow.loading && (
                                                            <small className="text-muted">…</small>
                                                        )}
                                                        {stripeRow && stripeRow.error && (
                                                            <small className="text-danger">{stripeRow.error}</small>
                                                        )}
                                                        {stripeMeta && (
                                                            <Label bsStyle={stripeMeta.style}>{stripeMeta.text}</Label>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Link
                                                            to={`/requests/detail/${r._id}`}
                                                            className="btn btn-info btn-xs"
                                                            style={{ marginRight: 4 }}
                                                        >
                                                            Voir
                                                        </Link>
                                                        <Button
                                                            bsSize="xsmall"
                                                            bsStyle="default"
                                                            onClick={() => checkStripe(r._id)}
                                                            disabled={stripeRow && stripeRow.loading}
                                                        >
                                                            Vérifier Stripe
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </Grid>
    );
};

export default PendingCapture;
